import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle,
  AudioLines,
  Bot,
  Brain,
  CircleHelp,
  Clock4,
  History,
  Info,
  MessageCircle,
  MessageSquare,
  PhoneCall,
  RotateCcw,
  Save,
  Settings2,
  Shield,
  Sparkles,
  Thermometer,
  Wand2,
  Zap,
} from "lucide-react";
import UserContext from "../../../../context/users";
import { AdminRole } from "../../../../constants";
import { updateAssistant } from "../../../../services/assistant";
import { getMyAvailableModels } from "../../../../services/available-models";
import { getAssistantVersions } from "../../../../services/assistant_version";
import { updatedSince } from "../../../../utils";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Select } from "../../../../components/ui/select";
import { Switch } from "../../../../components/ui/switch";
import { Slider } from "../../../../components/ui/slider";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Tooltip } from "../../../../components/ui/tooltip";
import { Spinner } from "../../../../components/ui/spinner";
import { Drawer } from "../../../../components/ui/drawer";
import { Dialog, DialogHeader, DialogContent, DialogFooter } from "../../../../components/ui/dialog";
import {
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
} from "../../../../components/ui/form-field";
import {
  createEditorSchema,
  getCustomPromptMaxLength,
  getPromptMaxLength,
  llmProviderOptions,
  rerankerProviderOptions,
  copilotProviderOptions,
  languageOptions,
  responseLimitOptions,
} from "./schema";
import {
  llmOptions,
  rerankerOptions,
  audioInterpreterOptions,
  audioProviderOptions,
  realtimeProviderOptions,
  realtimeModelOptions,
  realtimeVoiceOptions,
} from "./models";
import { cn } from "../../../../lib/cn";
import { toast } from 'sonner'

const PROVIDER_LABEL = {
  OPENAI: "OpenAI",
  GOOGLE: "Google",
  GROQ: "GROQ",
  DEEPSEEK: "Deepseek",
  FIREWORKS: "Fireworks",
  OPENROUTER: "OpenRouter",
  NVIDIA: "NVIDIA",
  VERBOO: "Verboo",
  MIMO: "MIMO",
};

export default function Editor({ data, setData, onOpenChat }) {
  const [user] = useContext(UserContext);
  const isAdmin = user.role === AdminRole;

  const [updating, setUpdating] = useState(false);
  const [userModels, setUserModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);

  const editorSchema = useMemo(() => createEditorSchema(isAdmin), [isAdmin]);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(editorSchema),
    defaultValues: withRealtimeDefaults(data),
    mode: "onBlur",
  });

  const isCustomPrompt = useWatch({ control, name: "isCustomPrompt" });
  const llmProvider = useWatch({ control, name: "llmProvider" });
  const llmModel = useWatch({ control, name: "llmModel" });
  const rerankerProvider = useWatch({ control, name: "rerankerProvider" });
  const audioProvider = useWatch({ control, name: "audioProvider" });
  const hasCopilot = useWatch({ control, name: "hasCopilot" });
  const copilotProvider = useWatch({ control, name: "copilotProvider" });
  const realtimeEnabled = useWatch({ control, name: "realtimeEnabled" });
  const realtimeProvider = useWatch({ control, name: "realtimeProvider" });
  const promptMaxLength = getPromptMaxLength(isAdmin, llmProvider);
  const customPromptMaxLength = getCustomPromptMaxLength(isAdmin);

  useEffect(() => {
    setModelsLoading(true);
    getMyAvailableModels()
      .then((response) => {
        const models = Array.isArray(response) ? response : response.data || [];
        setUserModels(
          models.map((m) => ({
            modelName: m.model,
            model: m.model,
            provider: m.provider,
          }))
        );
      })
      .catch((err) => {
        console.error("Failed to fetch user models", err);
        toast.error("Não foi possível carregar os modelos disponíveis");
      })
      .finally(() => setModelsLoading(false));
  }, []);

  useEffect(() => {
    reset(withRealtimeDefaults(data));
  }, [data, reset]);

  const onSubmit = useCallback(
    (values) => {
      setUpdating(true);
      updateAssistant(data.id, { ...data, ...values })
        .then(({ data: updated }) => {
          setData(updated);
          toast.success("Assistente atualizado com sucesso");
        })
        .catch((e) => {
          console.error(e);
          toast.error("Falha ao atualizar assistente");
        })
        .finally(() => setUpdating(false));
    },
    [data, setData]
  );

  const handleProviderChange = useCallback(
    (newProvider) => {
      setValue("llmProvider", newProvider, { shouldDirty: true });
      setValue("llmModel", llmOptions[newProvider]?.[0]?.value || "", { shouldDirty: true });
    },
    [setValue]
  );

  const handleRerankerChange = useCallback(
    (newProvider) => {
      setValue("rerankerProvider", newProvider, { shouldDirty: true });
      setValue("rerankerModel", rerankerOptions[newProvider]?.[0]?.value || "", { shouldDirty: true });
    },
    [setValue]
  );

  const handleCopilotChange = useCallback(
    (newProvider) => {
      setValue("copilotProvider", newProvider, { shouldDirty: true });
      setValue("copilotModel", rerankerOptions[newProvider]?.[0]?.value || "", { shouldDirty: true });
    },
    [setValue]
  );

  const handleUserModelClick = useCallback(
    (provider, model) => {
      setValue("llmProvider", provider, { shouldDirty: true });
      setValue("llmModel", model, { shouldDirty: true });
    },
    [setValue]
  );

  const llmModelOptions = useMemo(() => {
    const base = llmOptions[llmProvider] || [];
    const fromUser = userModels
      .filter((m) => m.provider === llmProvider)
      .map((m) => ({ label: m.modelName, value: m.model }));
    const seen = new Set();
    return [...base, ...fromUser].filter((opt) => {
      if (seen.has(opt.value)) return false;
      seen.add(opt.value);
      return true;
    });
  }, [llmProvider, userModels]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 pb-40 md:pb-24">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Settings2 size={18} style={{ color: "var(--dash-primary)" }} />
            <h2
              className="text-xl font-semibold tracking-tight"
              style={{ color: "var(--dash-text)" }}
            >
              Configurações do assistente
            </h2>
          </div>
          <p className="text-xs" style={{ color: "var(--dash-text-muted)" }}>
            Ajuste a personalidade, modelo de IA e comportamento do seu assistente.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          leftIcon={History}
          onClick={() => setVersionsOpen(true)}
        >
          Histórico de versões
        </Button>
      </header>

      {/* Identidade */}
      <SectionCard
        icon={Bot}
        title="Identidade"
        description="Nome, idioma e estado do assistente."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-2">
            <FormField
              control={control}
              name="name"
              render={({ field, error }) => (
                <FormItem>
                  <FormLabel htmlFor="name" required>Nome do assistente</FormLabel>
                  <Input
                    id="name"
                    placeholder="Ex: Atendente virtual"
                    invalid={!!error}
                    {...field}
                  />
                  <FormMessage error={error} />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={control}
            name="language"
            render={({ field, error }) => (
              <FormItem>
                <FormLabel>Idioma</FormLabel>
                <Select
                  value={field.value || "any"}
                  onChange={field.onChange}
                  disabled={!!isCustomPrompt}
                  options={languageOptions}
                  placeholder="Idioma"
                  invalid={!!error}
                />
                <FormMessage error={error} />
              </FormItem>
            )}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-6">
          <FormField
            control={control}
            name="isActive"
            render={({ field, error }) => (
              <div className="flex flex-col gap-1">
                <ToggleField
                  label="Ativo"
                  description="Controla se o assistente pode ser acessado externamente"
                  checked={!!field.value}
                  onChange={field.onChange}
                />
                <FormMessage error={error} />
              </div>
            )}
          />
          <FormField
            control={control}
            name="isCustomPrompt"
            render={({ field, error }) => (
              <div className="flex flex-col gap-1">
                <ToggleField
                  label="Prompt personalizado"
                  description="Use uma instrução totalmente personalizada"
                  checked={!!field.value}
                  onChange={field.onChange}
                />
                <FormMessage error={error} />
              </div>
            )}
          />
          {isAdmin && (
            <FormField
              control={control}
              name="maxInteractions"
              render={({ field, error }) => (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-0.5">
                      <FormLabel>Max. interações</FormLabel>
                      <FormDescription>1 a 10</FormDescription>
                    </div>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={field.value || ""}
                      invalid={!!error}
                      onChange={(e) =>
                        field.onChange(e.target.value ? Number(e.target.value) : null)
                      }
                      className="h-9 w-20 text-center"
                    />
                  </div>
                  <FormMessage error={error} />
                </div>
              )}
            />
          )}
        </div>
      </SectionCard>

      {/* Comportamento */}
      <SectionCard
        icon={Wand2}
        title="Comportamento"
        description={
          isCustomPrompt
            ? "Defina o prompt personalizado completo do assistente."
            : "Descreva como o assistente deve se comportar."
        }
      >
        {isCustomPrompt ? (
          <FormField
            control={control}
            name="customPrompt"
            render={({ field, error }) => (
              <FormItem>
                <FormLabel htmlFor="customPrompt" required>
                  Prompt personalizado
                </FormLabel>
                <Textarea
                  id="customPrompt"
                  rows={10}
                  maxLength={customPromptMaxLength}
                  showCount
                  placeholder={"Use $ para inserir variáveis. Ex: $\\{horario\\} ou $\\{conhecimento\\}"}
                  invalid={!!error}
                  {...field}
                  value={field.value || ""}
                />
                <FormDescription>
                  Variáveis disponíveis: <code className="font-mono">{"{horario}"}</code>,{" "}
                  <code className="font-mono">{"{conhecimento}"}</code>
                </FormDescription>
                <FormMessage error={error} />
              </FormItem>
            )}
          />
        ) : (
          <FormField
            control={control}
            name="prompt"
            render={({ field, error }) => (
              <FormItem>
                <FormLabel htmlFor="prompt" required>
                  Instrução
                </FormLabel>
                <Textarea
                  id="prompt"
                  rows={10}
                  maxLength={promptMaxLength}
                  showCount
                  placeholder="Você é um assistente atencioso e profissional..."
                  invalid={!!error}
                  {...field}
                  value={field.value || ""}
                />
                <FormMessage error={error} />
              </FormItem>
            )}
          />
        )}
      </SectionCard>

      {/* IA & Modelos (admin) */}
      {isAdmin && (
        <SectionCard
          icon={Brain}
          title="IA e Modelos"
          description="Configure os modelos de linguagem, áudio e reranking."
          badge={<Badge variant="primary">Admin</Badge>}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={control}
              name="audioProvider"
              render={({ field, error }) => (
                <FormItem>
                  <FormLabel>Interpretador de áudio</FormLabel>
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    options={audioProviderOptions}
                    placeholder="Provedor"
                    invalid={!!error}
                  />
                  <FormMessage error={error} />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="audioModel"
              render={({ field, error }) => (
                <FormItem>
                  <FormLabel>Modelo de áudio</FormLabel>
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    options={audioInterpreterOptions[audioProvider] || []}
                    placeholder="Modelo"
                    invalid={!!error}
                  />
                  <FormMessage error={error} />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="llmProvider"
              render={({ field, error }) => (
                <FormItem>
                  <FormLabel>Provedor de LLM</FormLabel>
                  <Select
                    value={field.value}
                    onChange={handleProviderChange}
                    options={llmProviderOptions}
                    placeholder="Provedor"
                    searchable
                    invalid={!!error}
                  />
                  <FormMessage error={error} />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="llmModel"
              render={({ field, error }) => (
                <FormItem>
                  <FormLabel>Modelo LLM</FormLabel>
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    options={llmModelOptions}
                    placeholder="Modelo"
                    searchable
                    invalid={!!error}
                  />
                  <FormMessage error={error} />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="rerankerProvider"
              render={({ field, error }) => (
                <FormItem>
                  <FormLabel>Provedor de reranking</FormLabel>
                  <Select
                    value={field.value}
                    onChange={handleRerankerChange}
                    options={rerankerProviderOptions}
                    placeholder="Provedor"
                    invalid={!!error}
                  />
                  <FormMessage error={error} />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="rerankerModel"
              render={({ field, error }) => (
                <FormItem>
                  <FormLabel>Modelo de reranking</FormLabel>
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    options={rerankerOptions[rerankerProvider] || []}
                    placeholder="Modelo"
                    invalid={!!error}
                  />
                  <FormMessage error={error} />
                </FormItem>
              )}
            />
          </div>
        </SectionCard>
      )}

      {/* Voz Realtime (admin) */}
      {isAdmin && (
        <SectionCard
          icon={AudioLines}
          title="Voz ao vivo"
          description="Conversa bidirecional de baixa latência usando WebRTC."
          badge={<Badge variant="primary">Realtime</Badge>}
          action={
            <FormField
              control={control}
              name="realtimeEnabled"
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <span className="hidden text-[11px] text-[var(--dash-text-muted)] sm:inline">
                    {field.value ? "Habilitado" : "Desabilitado"}
                  </span>
                  <Switch checked={!!field.value} onChange={field.onChange} />
                </div>
              )}
            />
          }
        >
          <div
            className={cn(
              "flex flex-col gap-5 transition-opacity duration-200",
              !realtimeEnabled && "pointer-events-none opacity-40",
            )}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <FormField
                control={control}
                name="realtimeProvider"
                render={({ field, error }) => (
                  <FormItem>
                    <FormLabel>Provedor Realtime</FormLabel>
                    <Select
                      value={field.value || "OPENAI"}
                      onChange={(provider) => {
                        field.onChange(provider);
                        setValue(
                          "realtimeModel",
                          realtimeModelOptions[provider]?.[0]?.value || "",
                          { shouldDirty: true },
                        );
                        setValue(
                          "realtimeVoice",
                          realtimeVoiceOptions[provider]?.[0]?.value || "",
                          { shouldDirty: true },
                        );
                      }}
                      options={realtimeProviderOptions}
                      invalid={!!error}
                    />
                    <FormMessage error={error} />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="realtimeModel"
                render={({ field, error }) => (
                  <FormItem>
                    <FormLabel>Modelo de voz</FormLabel>
                    <Select
                      value={field.value || "gpt-realtime-2.1"}
                      onChange={field.onChange}
                      options={realtimeModelOptions[realtimeProvider || "OPENAI"] || []}
                      invalid={!!error}
                    />
                    <FormMessage error={error} />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="realtimeVoice"
                render={({ field, error }) => (
                  <FormItem>
                    <FormLabel>Voz</FormLabel>
                    <Select
                      value={field.value || "marin"}
                      onChange={field.onChange}
                      options={realtimeVoiceOptions[realtimeProvider || "OPENAI"] || []}
                      invalid={!!error}
                    />
                    <FormMessage error={error} />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="realtimeTranscriptionModel"
                render={({ field, error }) => (
                  <FormItem>
                    <FormLabel>Transcrição de entrada</FormLabel>
                    <Select
                      value={field.value || "gpt-4o-mini-transcribe"}
                      onChange={field.onChange}
                      options={audioInterpreterOptions.OPENAI}
                      invalid={!!error}
                    />
                    <FormMessage error={error} />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="realtimeMaxDurationSeconds"
                render={({ field, error }) => (
                  <FormItem>
                    <FormLabel>Duração máxima</FormLabel>
                    <Input
                      type="number"
                      min={60}
                      max={3600}
                      step={60}
                      value={field.value ?? 3600}
                      invalid={!!error}
                      onChange={(event) => field.onChange(Number(event.target.value))}
                    />
                    <FormDescription>Entre 60 e 3.600 segundos</FormDescription>
                    <FormMessage error={error} />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-[var(--dash-primary-border)] bg-[var(--dash-primary-soft)] p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <Shield size={16} className="mt-0.5 shrink-0 text-[var(--dash-primary)]" />
                <div>
                  <p className="text-xs font-semibold text-[var(--dash-text)]">
                    Credencial centralizada no servidor
                  </p>
                  <p className="mt-1 text-[11px] leading-4 text-[var(--dash-text-muted)]">
                    O navegador recebe apenas a negociação WebRTC. RAG, ferramentas, histórico e autenticação continuam no backend.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={PhoneCall}
                onClick={() => onOpenChat?.("voice")}
              >
                Testar voz
              </Button>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Modelos do usuário */}
      <SectionCard
        icon={Sparkles}
        title="Modelos disponíveis"
        description="Selecione um dos modelos liberados para sua conta."
      >
        {modelsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size={18} />
          </div>
        ) : userModels.length === 0 ? (
          <p
            className="py-2 text-xs"
            style={{ color: "var(--dash-text-muted)" }}
          >
            Nenhum modelo disponível para sua conta.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {userModels.map(({ model, modelName, provider }) => {
              const isSelected = llmProvider === provider && llmModel === model;
              return (
                <button
                  key={provider + model}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => handleUserModelClick(provider, model)}
                  className={cn(
                    "group relative flex flex-col gap-2 rounded-xl p-4 text-left transition-all duration-200",
                    isSelected ? "scale-[1.02]" : "hover:scale-[1.01]"
                  )}
                  style={{
                    background: isSelected
                      ? "var(--dash-primary-soft)"
                      : "var(--dash-surface-subtle)",
                    border: isSelected
                      ? "1px solid var(--dash-primary)"
                      : "1px solid var(--dash-border)",
                    boxShadow: isSelected
                      ? "0 0 24px rgba(173,52,254,0.18)"
                      : "none",
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="text-[10px] font-medium uppercase tracking-[0.12em]"
                      style={{
                        color: isSelected
                          ? "var(--dash-primary)"
                          : "var(--dash-text-muted)",
                      }}
                    >
                      {PROVIDER_LABEL[provider] || provider}
                    </span>
                    {isSelected && (
                      <span
                        className="rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                        style={{
                          background: "var(--dash-primary)",
                          color: "#fff",
                        }}
                      >
                        ATIVO
                      </span>
                    )}
                  </div>
                  <span
                    className="truncate text-sm font-semibold"
                    style={{ color: "var(--dash-text)" }}
                  >
                    {modelName}
                  </span>
                </button>
              );
            })}
            {!userModels.find((item) => item.model === llmModel) && !!llmModel && (
              <div
                className="col-span-full flex items-center gap-3 rounded-xl p-4"
                style={{
                  background: "var(--dash-primary-soft)",
                  border: "1px solid var(--dash-primary)",
                }}
              >
                <Settings2 size={16} style={{ color: "var(--dash-primary)" }} />
                <div className="flex flex-col">
                  <span
                    className="text-[10px] font-medium uppercase tracking-[0.12em]"
                    style={{ color: "var(--dash-primary)" }}
                  >
                    Modelo customizado
                  </span>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: "var(--dash-text)" }}
                  >
                    {llmOptions[llmProvider]?.find((item) => item.value === llmModel)?.label || llmModel}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Resposta */}
      <SectionCard
        icon={Thermometer}
        title="Controle de resposta"
        description="Ajuste como o assistente gera as respostas."
      >
        <div className="flex flex-col gap-6">
          <FormField
            control={control}
            name="temperature"
            render={({ field, error }) => (
              <div className="flex flex-col gap-1">
                <Slider
                  label="Temperatura"
                  description="Maior = mais criativo, menor = mais previsível"
                  min={0}
                  max={2}
                  step={0.01}
                  value={field.value ?? 0.5}
                  onChange={field.onChange}
                  formatValue={(v) => Number(v).toFixed(2)}
                  marks={[
                    { value: 0, label: "0" },
                    { value: 1, label: "1" },
                    { value: 2, label: "2" },
                  ]}
                />
                <FormMessage error={error} />
              </div>
            )}
          />
          {isAdmin && (
            <FormField
              control={control}
              name="topP"
              render={({ field, error }) => (
                <div className="flex flex-col gap-1">
                  <Slider
                    label="Top-P"
                    description="Probabilidade acumulada para amostragem"
                    min={0}
                    max={1}
                    step={0.01}
                    value={field.value ?? 0.5}
                    onChange={field.onChange}
                    formatValue={(v) => Number(v).toFixed(2)}
                    marks={[
                      { value: 0, label: "0" },
                      { value: 0.5, label: "0.5" },
                      { value: 1, label: "1" },
                    ]}
                  />
                  <FormMessage error={error} />
                </div>
              )}
            />
          )}

          <FormField
            control={control}
            name="restrictedByContext"
            render={({ field, error }) => (
              <div className="flex flex-col gap-1">
                <ToggleField
                  label="Restrito ao contexto"
                  description="Respostas limitadas aos documentos da base de conhecimento"
                  icon={Shield}
                  checked={!!field.value}
                  onChange={field.onChange}
                  disabled={!!isCustomPrompt}
                />
                <FormMessage error={error} />
              </div>
            )}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              control={control}
              name="maxTokens"
              render={({ field, error }) => (
                <FormItem>
                  <FormLabel>Limite de resposta</FormLabel>
                  <Select
                    value={field.value ?? 300}
                    onChange={field.onChange}
                    options={responseLimitOptions}
                    invalid={!!error}
                  />
                  <FormMessage error={error} />
                </FormItem>
              )}
            />
            {isAdmin && (
              <FormField
                control={control}
                name="historyLimit"
                render={({ field, error }) => (
                  <FormItem>
                    <FormLabel>Limite de histórico</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      placeholder="Ex: 10"
                      value={field.value ?? ""}
                      invalid={!!error}
                      onChange={(e) =>
                        field.onChange(e.target.value ? Number(e.target.value) : null)
                      }
                    />
                    <FormDescription>
                      Número máximo de mensagens usadas como contexto
                    </FormDescription>
                    <FormMessage error={error} />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>
      </SectionCard>

      {/* Copiloto */}
      {isAdmin && (
        <SectionCard
          icon={MessageSquare}
          title="Configurações de copiloto"
          description="Modelo dedicado para operação como copiloto de atendimento."
          badge={<Badge variant="primary">Admin</Badge>}
          action={
            <FormField
              control={control}
              name="hasCopilot"
              render={({ field }) => (
                <Switch checked={!!field.value} onChange={field.onChange} />
              )}
            />
          }
        >
          <div
            className={cn(
              "flex flex-col gap-4 transition-opacity duration-300",
              !hasCopilot && "pointer-events-none opacity-30"
            )}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={control}
                name="copilotProvider"
                render={({ field, error }) => (
                  <FormItem>
                    <FormLabel>Provedor de copiloto</FormLabel>
                    <Select
                      value={field.value}
                      onChange={handleCopilotChange}
                      options={copilotProviderOptions}
                      placeholder="Provedor"
                      invalid={!!error}
                    />
                    <FormMessage error={error} />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="copilotModel"
                render={({ field, error }) => (
                  <FormItem>
                    <FormLabel>Modelo</FormLabel>
                    <Select
                      value={field.value}
                      onChange={field.onChange}
                      options={rerankerOptions[copilotProvider] || []}
                      placeholder="Modelo"
                      invalid={!!error}
                    />
                    <FormMessage error={error} />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={control}
              name="copilotPrompt"
              render={({ field, error }) => (
                <FormItem>
                  <FormLabel>Instrução de copiloto</FormLabel>
                  <Textarea
                    rows={5}
                    placeholder="Quando a flag copilot for true na API, este prompt será usado..."
                    invalid={!!error}
                    {...field}
                    value={field.value || ""}
                  />
                  <FormMessage error={error} />
                </FormItem>
              )}
            />
          </div>
        </SectionCard>
      )}

      {/* Sticky save bar */}
      <div
        className={cn(
          "fixed right-0 z-40 border-t border-[var(--dash-border)] bg-[var(--dash-bg)]/92 backdrop-blur-md",
          "max-md:left-0 max-md:bottom-[68px]",
          "md:left-[var(--sidebar-width,56px)] md:bottom-0",
        )}
      >
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-4 px-4 py-3 md:px-6 md:py-4">
          <div className="flex items-center gap-2">
            {Object.keys(errors).length > 0 ? (
              <>
                <AlertCircle size={14} className="shrink-0 text-[#d61f3f]" />
                <span className="text-xs font-medium text-[#d61f3f]">
                  {Object.keys(errors).length === 1
                    ? "1 campo com erro"
                    : `${Object.keys(errors).length} campos com erro`}
                </span>
              </>
            ) : isDirty ? (
              <>
                <Info size={14} className="text-[var(--dash-primary)]" />
                <span className="text-xs font-medium text-[var(--dash-primary)]">
                  Você tem alterações não salvas
                </span>
              </>
            ) : (
              <span className="text-xs text-[var(--dash-text-muted)]">
                Tudo sincronizado
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              variant="primary"
              leftIcon={Save}
              loading={updating}
              disabled={updating}
            >
              Salvar alterações
            </Button>
          </div>
        </div>
      </div>

      <VersionsDrawer
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        id={data.id}
        applyVersion={(v) => reset({ ...watch(), ...v })}
        updatedAt={data.updatedAt}
      />
    </form>
  );
}

function SectionCard({ icon: Icon, title, description, badge, action, children }) {
  return (
    <Card className="overflow-visible">
      <CardHeader className="flex-row items-start justify-between gap-4 pb-3">
        <div className="flex items-start gap-3">
          {Icon && (
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
              style={{
                background: "var(--dash-primary-soft)",
                border: "1px solid var(--dash-primary-border)",
                color: "var(--dash-primary)",
              }}
            >
              <Icon size={16} strokeWidth={2.2} />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <CardTitle>{title}</CardTitle>
              {badge}
            </div>
            {description && <CardDescription>{description}</CardDescription>}
          </div>
        </div>
        {action}
      </CardHeader>
      <CardContent className="pt-2">{children}</CardContent>
    </Card>
  );
}

function ToggleField({ label, description, icon: Icon, checked, onChange, disabled }) {
  return (
    <div className="flex items-center gap-3">
      <Switch checked={checked} onChange={onChange} disabled={disabled} />
      <div className="flex flex-col gap-0.5">
        <span
          className="flex items-center gap-1.5 text-xs font-semibold"
          style={{ color: "var(--dash-text)" }}
        >
          {Icon && <Icon size={12} />}
          {label}
        </span>
        {description && (
          <span
            className="text-[11px]"
            style={{ color: "var(--dash-text-muted)" }}
          >
            {description}
          </span>
        )}
      </div>
    </div>
  );
}

function VersionsDrawer({ open, onClose, id, applyVersion, updatedAt }) {
  const [versions, setVersions] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 3;
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  const fetchVersions = useCallback(
    (pageNumber) => {
      setLoading(true);
      getAssistantVersions(id, { page: pageNumber, perPage })
        .then(({ data, total }) => {
          setVersions((prev) => (pageNumber === 1 ? data : [...prev, ...data]));
          setTotal(total);
          setPage(pageNumber);
        })
        .catch((err) => {
          console.error(err);
          toast.error("Falha ao carregar versões");
        })
        .finally(() => setLoading(false));
    },
    [id]
  );

  useEffect(() => {
    if (!open || !id) return;
    setVersions([]);
    setPage(1);
    setTotal(0);
    fetchVersions(1);
  }, [open, id, updatedAt, fetchVersions]);

  const handleRestore = useCallback(
    (version) => {
      applyVersion(version);
      toast.info("Versão carregada");
      onClose?.();
    },
    [applyVersion, onClose]
  );

  const hasMore = versions.length < total;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        side="right"
        size="md"
        title="Histórico de versões"
        description="Restaure parâmetros de versões anteriores do assistente"
      >
        <div className="flex flex-col gap-4 p-6">
          {loading && versions.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size={18} />
          </div>
        ) : versions.length === 0 ? (
          <p className="text-xs" style={{ color: "var(--dash-text-muted)" }}>
            Nenhuma versão salva ainda.
          </p>
        ) : (
          <div className="relative flex flex-col gap-0">
            <div
              className="absolute bottom-0 left-[11px] top-2 w-px"
              style={{ background: "var(--dash-border)" }}
            />
            {versions.map((item, i) => (
              <div key={item.id} className="relative flex gap-4 pb-6">
                <div
                  className="relative z-10 mt-1 h-6 w-6 shrink-0 rounded-full"
                  style={{
                    background: i === 0 ? "var(--dash-primary)" : "var(--dash-surface)",
                    border:
                      i === 0
                        ? "2px solid var(--dash-primary)"
                        : "2px solid var(--dash-border-strong)",
                    boxShadow:
                      i === 0 ? "0 0 16px rgba(173,52,254,0.4)" : "none",
                  }}
                >
                  {i === 0 && (
                    <Clock4
                      size={10}
                      strokeWidth={2.5}
                      color="#fff"
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    />
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="text-xs font-medium"
                      style={{ color: "var(--dash-text)" }}
                    >
                      {updatedSince(new Date(item.createdAt))} atrás
                    </span>
                    <Button
                      variant={i === 0 ? "primary" : "outline"}
                      size="sm"
                      leftIcon={i === 0 ? null : RotateCcw}
                      onClick={() => handleRestore(item)}
                    >
                      Restaurar
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[11px] tabular-nums"
                      style={{ color: "var(--dash-text-muted)" }}
                    >
                      {(item.prompt ? item.prompt.length : 0)} caracteres
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedVersion(item)}
                      className="text-[11px] font-medium transition-colors"
                      style={{ color: "var(--dash-primary)" }}
                    >
                      Ver detalhes
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {hasMore && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchVersions(page + 1)}
            loading={loading}
          >
            Carregar mais
          </Button>
        )}
        </div>
      </Drawer>

      <Dialog open={!!selectedVersion} onClose={() => setSelectedVersion(null)} size="lg">
        <DialogHeader
          icon={History}
          title="Detalhes da versão"
          description="Revise os parâmetros desta versão e restaure se desejar."
        />
        <DialogContent>
          {selectedVersion && (
            <div className="dash-scroll grid max-h-[60vh] grid-cols-1 gap-3 overflow-y-auto pr-2 sm:grid-cols-2">
              <DetailItem label="Nome" value={selectedVersion.name} />
              <DetailItem label="Linguagem" value={selectedVersion.language} />
              <DetailItem label="Provedor LLM" value={selectedVersion.llmProvider} />
              <DetailItem label="Modelo LLM" value={selectedVersion.llmModel} />
              <DetailItem label="Provedor Áudio" value={selectedVersion.audioProvider} />
              <DetailItem label="Modelo Áudio" value={selectedVersion.audioModel} />
              <DetailItem
                label="Realtime"
                value={selectedVersion.realtimeEnabled ? "Habilitado" : "Desabilitado"}
              />
              <DetailItem label="Provider Realtime" value={selectedVersion.realtimeProvider} />
              <DetailItem label="Modelo Realtime" value={selectedVersion.realtimeModel} />
              <DetailItem label="Voz Realtime" value={selectedVersion.realtimeVoice} />
              <DetailItem
                label="Transcrição Realtime"
                value={selectedVersion.realtimeTranscriptionModel}
              />
              <DetailItem
                label="Duração Realtime"
                value={
                  selectedVersion.realtimeMaxDurationSeconds
                    ? `${selectedVersion.realtimeMaxDurationSeconds}s`
                    : null
                }
              />
              <DetailItem label="Temperatura" value={selectedVersion.temperature} />
              <DetailItem label="Top-p" value={selectedVersion.top_p} />
              <DetailItem label="Max tokens" value={selectedVersion.maxTokens} />
              <DetailItem label="Histórico" value={selectedVersion.historyLimit} />
              <DetailItem
                label="Restrito ao contexto"
                value={selectedVersion.restrictedByContext ? "Sim" : "Não"}
              />
              <DetailItem
                label="Personalizado"
                value={selectedVersion.isCustomPrompt ? "Sim" : "Não"}
              />
              <div className="col-span-full">
                <DetailItem
                  label="Prompt"
                  value={selectedVersion.prompt}
                  multiline
                />
              </div>
              {selectedVersion.customPrompt && (
                <div className="col-span-full">
                  <DetailItem
                    label="Prompt personalizado"
                    value={selectedVersion.customPrompt}
                    multiline
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setSelectedVersion(null)}>
            Fechar
          </Button>
          <Button
            variant="primary"
            leftIcon={RotateCcw}
            onClick={() => {
              handleRestore(selectedVersion);
              setSelectedVersion(null);
            }}
          >
            Restaurar versão
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
}

function withRealtimeDefaults(data = {}) {
  return {
    ...data,
    temperature: data?.temperature ?? 0.5,
    topP: data?.topP ?? 0.5,
    realtimeEnabled: data?.realtimeEnabled ?? false,
    realtimeProvider: data?.realtimeProvider || "OPENAI",
    realtimeModel: data?.realtimeModel || "gpt-realtime-2.1",
    realtimeVoice: data?.realtimeVoice || "marin",
    realtimeTranscriptionModel:
      data?.realtimeTranscriptionModel || "gpt-4o-mini-transcribe",
    realtimeMaxDurationSeconds: data?.realtimeMaxDurationSeconds || 3600,
  };
}

function DetailItem({ label, value, multiline }) {
  return (
    <div
      className="flex flex-col gap-1 rounded-lg px-3 py-2"
      style={{
        background: "var(--dash-surface-subtle)",
        border: "1px solid var(--dash-border)",
      }}
    >
      <span
        className="text-[10px] font-medium uppercase tracking-[0.12em]"
        style={{ color: "var(--dash-text-muted)" }}
      >
        {label}
      </span>
      <span
        className={cn(
          "break-words text-xs",
          multiline ? "whitespace-pre-line" : "truncate"
        )}
        style={{ color: "var(--dash-text)" }}
      >
        {value || "—"}
      </span>
    </div>
  );
}
